import type { BulkDraft, BulkDraftEmail } from '~/api/data/email-bulk-send/queries';
import { EmailBulkSendDraftState } from '~/api/graphql';

export type BulkDraftRecipient = BulkDraft['recipients'][number];

export type BulkDraftRecipientStatus =
  | 'drafting'
  | 'ready'
  | 'heldBack'
  | 'couldNotDraft'
  | 'notDrafted';

export const bulkDraftRecipientStatus = (
  recipient: BulkDraftRecipient
): BulkDraftRecipientStatus => {
  switch (recipient.draftState) {
    case EmailBulkSendDraftState.Drafting:
      return 'drafting';
    case EmailBulkSendDraftState.Failed:
      return 'couldNotDraft';
    case EmailBulkSendDraftState.NotDrafted:
      return 'notDrafted';
    case EmailBulkSendDraftState.Drafted:
      return recipient.heldBackReason ? 'heldBack' : 'ready';
  }
};

/** Who a row's actions name to a screen reader, as "Open client1@mail.example". */
export const recipientLabel = (recipient: BulkDraftRecipient) =>
  recipient.email ?? recipient.name ?? 'this recipient';

/**
 * The emails Send all, Schedule all and Move all to drafts take; every other one is held back
 * and stays a draft. A held-back email isn't moved either: a draft in the mailbox can be sent
 * without Cara's checks.
 */
export const isReadyToSend = (recipient: BulkDraftRecipient) =>
  bulkDraftRecipientStatus(recipient) === 'ready';

/** Whether Cara has drafted this recipient's email, so it has a subject and a template. */
export const hasDraftedEmail = (recipient: BulkDraftRecipient) => {
  const status = bulkDraftRecipientStatus(recipient);
  return status === 'ready' || status === 'heldBack';
};

export type BulkDraftTemplates =
  | { kind: 'shared'; templateId: string }
  | { kind: 'none' }
  | { kind: 'mixed' };

/**
 * Whether the drafted emails share one template, shown once in the header, or differ, shown
 * per email in a Template column.
 */
export const bulkDraftTemplates = (
  recipients: BulkDraftRecipient[],
  emailByArtifactId: Record<string, BulkDraftEmail>
): BulkDraftTemplates => {
  // Emails whose read failed say nothing either way.
  const templateIds = new Set(
    recipients
      .filter(hasDraftedEmail)
      .flatMap((recipient) => emailByArtifactId[recipient.artifactId ?? ''] ?? [])
      .map(({ templateId }) => templateId)
  );
  const [onlyTemplateId] = templateIds;
  if (templateIds.size > 1) return { kind: 'mixed' };
  return onlyTemplateId ? { kind: 'shared', templateId: onlyTemplateId } : { kind: 'none' };
};
